import styled from "@emotion/styled";
import { useNavigate } from "react-router";

import { atlasRoutes } from "@/app/router/routes";

const Section = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  background: #fff;
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 16px 0;
`;

const EntityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const EntityRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
  }
`;

const EntityLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
`;

const EntityValue = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #1e293b;
  font-family: "Berkeley Mono Variable", monospace;
`;

const EntityLink = styled.button`
  font-size: 13px;
  font-weight: 500;
  color: #2563eb;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: "Berkeley Mono Variable", monospace;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: #1d4ed8;
  }
`;

const NoValue = styled.span`
  font-size: 13px;
  color: #9ca3af;
`;

interface RelatedEntitiesSectionProps {
  spaceAccountId: string;
  capturedAudioId: string;
  associatedNoteId: string | null;
  associatedSessionId: string | null;
  associatedClientId: string | null;
}

export const RelatedEntitiesSection = ({
  spaceAccountId,
  capturedAudioId,
  associatedNoteId,
  associatedSessionId,
  associatedClientId,
}: RelatedEntitiesSectionProps) => {
  const navigate = useNavigate();

  return (
    <Section>
      <SectionTitle>Related Entities</SectionTitle>
      <EntityList>
        <EntityRow>
          <EntityLabel>Space Account</EntityLabel>
          <EntityLink
            onClick={() =>
              navigate(atlasRoutes.spaceAccountsView({ params: { spaceAccountId } }).path)
            }
          >
            {spaceAccountId.slice(0, 8)}...
          </EntityLink>
        </EntityRow>

        <EntityRow>
          <EntityLabel>Captured Audio</EntityLabel>
          <EntityLink
            onClick={() =>
              navigate(atlasRoutes.capturedAudioView({ params: { capturedAudioId } }).path)
            }
          >
            {capturedAudioId.slice(0, 8)}...
          </EntityLink>
        </EntityRow>

        <EntityRow>
          <EntityLabel>Associated Note</EntityLabel>
          {associatedNoteId ? (
            <EntityLink
              onClick={() =>
                navigate(atlasRoutes.notesView({ params: { noteId: associatedNoteId } }).path)
              }
            >
              {associatedNoteId.slice(0, 8)}...
            </EntityLink>
          ) : (
            <NoValue>None</NoValue>
          )}
        </EntityRow>

        <EntityRow>
          <EntityLabel>Session</EntityLabel>
          {associatedSessionId ? (
            <EntityLink
              onClick={() =>
                navigate(
                  atlasRoutes.sessionEvents({ params: { sessionId: associatedSessionId } }).path
                )
              }
            >
              {associatedSessionId.slice(0, 8)}...
            </EntityLink>
          ) : (
            <NoValue>None</NoValue>
          )}
        </EntityRow>

        <EntityRow>
          <EntityLabel>Client ID</EntityLabel>
          {associatedClientId ? (
            <EntityValue>{associatedClientId.slice(0, 8)}...</EntityValue>
          ) : (
            <NoValue>None</NoValue>
          )}
        </EntityRow>
      </EntityList>
    </Section>
  );
};
